import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Stamp, Loader2, ArrowUp, ArrowDown, X, BellRing, Repeat, Download, Ban, Lock, Paperclip, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";
import { downloadFile } from "@/lib/enquiries";

const STEP_STYLES = {
  locked: "bg-slate-100 text-slate-500 border-slate-200",
  pending: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  opened: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
  approved: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  rejected: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};
const CHAIN_LABELS = { in_progress: "In Progress", approved: "Approved", rejected: "Rejected", cancelled: "Cancelled", review_required: "Review Required" };

export default function ApprovalChainPanel({ enquiryId, invites }) {
  const [data, setData] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [chain, setChain] = useState([]);
  const [comment, setComment] = useState("");
  const [deadline, setDeadline] = useState("");
  const [recSeller, setRecSeller] = useState("");
  const [recReason, setRecReason] = useState("");
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignForm, setReassignForm] = useState({ new_approver_id: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const [approvalFiles, setApprovalFiles] = useState([]);
  const [approvalNote, setApprovalNote] = useState("");
  const approvalFileRef = useRef(null);

  const loadFiles = useCallback(() => {
    api.get(`/enquiries/${enquiryId}/approval/attachments`).then((r) => setApprovalFiles(r.data.attachments || [])).catch(() => {});
  }, [enquiryId]);

  const load = useCallback(() => {
    api.get(`/enquiries/${enquiryId}/approval`).then((r) => setData(r.data)).catch(() => {});
    api.get("/approvers").then((r) => setApprovers(r.data.approvers)).catch(() => {});
    loadFiles();
  }, [enquiryId, loadFiles]);

  const uploadApprovalFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("note", approvalNote);
    try {
      await api.post(`/enquiries/${enquiryId}/approval/attachments`, fd);
      toast.success("Approval attachment added — approvers can view it");
      setApprovalNote("");
      loadFiles();
    } catch (err) { toast.error(formatApiError(err)); } finally { e.target.value = ""; }
  };
  useEffect(() => { load(); }, [load]);

  const participated = (invites || []).filter((i) => i.status === "participated");
  const activeApprovers = approvers.filter((a) => a.status === "active");

  const move = (i, dir) => {
    const next = [...chain];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setChain(next);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!chain.length) return toast.error("Add at least one approver to the chain");
    setSaving(true);
    try {
      await api.post(`/enquiries/${enquiryId}/approval`, {
        approver_ids: chain.map((a) => a.id), buyer_comment: comment,
        deadline: deadline ? new Date(deadline).toISOString() : null,
        recommended_seller_id: recSeller || null, recommended_reason: recReason,
      });
      toast.success("Approval request sent — first approver notified (email simulated)");
      setSendOpen(false);
      setChain([]); setComment(""); setDeadline(""); setRecSeller(""); setRecReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const chainData = data?.chain;
  const steps = data?.steps || [];

  return (
    <div className="rounded-lg border bg-card" data-testid="approval-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3">
        <h2 className="font-heading text-base font-semibold flex items-center gap-2"><Stamp className="h-4 w-4 text-slate-500" /> Approval</h2>
        <div className="flex items-center gap-2">
          {chainData && (
            <Button variant="outline" size="sm" data-testid="download-approval-pdf-btn"
              onClick={() => downloadFile(`/enquiries/${enquiryId}/approval/pdf`, `approval-summary.pdf`)}>
              <Download className="mr-1.5 h-3.5 w-3.5" /> Approval Summary
            </Button>
          )}
          {!chainData && (
            <Button size="sm" data-testid="send-for-approval-btn" disabled={!participated.length}
              onClick={() => setSendOpen(true)} className="bg-slate-900 hover:bg-slate-800">
              <Stamp className="mr-1.5 h-4 w-4" /> Send for Approval
            </Button>
          )}
        </div>
      </div>

      <div className="border-b px-5 py-3" data-testid="approval-attachments-block">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Attachments (shared with approvers — optional)</p>
        <div className="flex flex-wrap items-center gap-2">
          {approvalFiles.map((f) => (
            <button key={f.id} data-testid={`approval-doc-${f.id.slice(0, 8)}`} onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}
              className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-slate-50">
              <Download className="h-3 w-3" /> {f.filename}{f.note ? ` — ${f.note}` : ""}
            </button>
          ))}
          <input ref={approvalFileRef} type="file" className="hidden" data-testid="approval-attachment-input" onChange={uploadApprovalFile} />
          <Input data-testid="approval-attachment-note" className="h-8 w-52 text-xs" placeholder="Note (e.g. Budget Approval)" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} />
          <Button variant="outline" size="sm" data-testid="add-approval-attachment-btn" onClick={() => approvalFileRef.current?.click()}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add Attachment
          </Button>
        </div>
      </div>

      {!chainData ? (
        <div className="px-5 py-5" data-testid="approval-empty">
          <p className="text-sm text-muted-foreground">Not started. Send this event for internal approval once seller quotations and evaluations are ready.</p>
          {data?.tech_incomplete && <p className="mt-2 rounded-md border border-[#FDE68A] bg-[#FEF3C7]/50 px-3 py-2 text-xs text-[#92400E]" data-testid="tech-incomplete-warning">Technical evaluation is incomplete — pending technical reviews must finish first.</p>}
        </div>
      ) : (
        <div className="px-5 py-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="approval-chain-status" className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${chainData.status === "approved" ? STEP_STYLES.approved : chainData.status === "rejected" ? STEP_STYLES.rejected : chainData.status === "review_required" ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : STEP_STYLES.pending}`}>
              {CHAIN_LABELS[chainData.status] || chainData.status}
            </span>
            {chainData.deadline && <span className="text-xs text-muted-foreground">Deadline: {fmtDateTime(chainData.deadline)}</span>}
            {chainData.buyer_comment && <span className="w-full text-xs text-muted-foreground">Buyer comment: "{chainData.buyer_comment}"</span>}
            {chainData.recommended_seller_id && (
              <span className="w-full text-xs" data-testid="recommended-seller">Recommended seller: <span className="font-medium">{participated.find((p) => p.supplier_company_id === chainData.recommended_seller_id)?.supplier_name || chainData.recommended_seller_id}</span>
                {chainData.recommended_reason && <span className="text-muted-foreground"> — {chainData.recommended_reason}</span>}</span>
            )}
          </div>
          <div className="space-y-0" data-testid="approval-chain-steps">
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-stretch gap-3" data-testid={`approval-step-${s.sequence_number}`}>
                <div className="flex flex-col items-center">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${STEP_STYLES[s.overdue ? "pending" : s.status] || STEP_STYLES.pending}`}>{s.sequence_number}</div>
                  {i < steps.length - 1 && <div className="w-px flex-1 bg-slate-200" />}
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">{s.approver_name}</p>
                    <span className="text-xs text-muted-foreground">{s.approver_designation}</span>
                    <span data-testid={`step-status-${s.sequence_number}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STEP_STYLES[s.status] || STEP_STYLES.pending}`}>
                      {s.overdue ? "Overdue" : s.status}
                    </span>
                    {s.status === "locked" && <Lock className="h-3 w-3 text-slate-400" />}
                  </div>
                  {s.decided_at && <p className="text-xs text-muted-foreground">{s.decision} · {fmtDateTime(s.decided_at)}</p>}
                  {s.comment && <p className="mt-0.5 rounded bg-slate-50 px-2 py-1 text-xs" data-testid={`step-comment-${s.sequence_number}`}>"{s.comment}"</p>}
                  {(s.reassignment_history || []).map((h) => (
                    <p key={`${h.previous_approver}|${h.reason}`} className="text-[10px] text-muted-foreground">Reassigned from {h.previous_approver} — {h.reason}</p>
                  ))}
                  {(s.status === "pending" || s.status === "opened") && (
                    <div className="mt-1.5 flex gap-1.5">
                      <Button variant="outline" size="sm" data-testid={`remind-step-${s.sequence_number}`}
                        onClick={() => act(() => api.post(`/enquiries/${enquiryId}/approval/steps/${s.id}/remind`), "Reminder sent (email simulated)")}>
                        <BellRing className="mr-1 h-3 w-3" /> Remind
                      </Button>
                      <Button variant="outline" size="sm" data-testid={`reassign-step-${s.sequence_number}`}
                        onClick={() => { setReassignTarget(s); setReassignForm({ new_approver_id: "", reason: "" }); }}>
                        <Repeat className="mr-1 h-3 w-3" /> Reassign
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {(chainData.status === "in_progress" || chainData.status === "review_required") && (
            <Button variant="outline" size="sm" data-testid="cancel-approval-btn" className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => act(() => api.post(`/enquiries/${enquiryId}/approval/cancel`), "Approval process cancelled")}>
              <Ban className="mr-1 h-3.5 w-3.5" /> Cancel Approval Process
            </Button>
          )}
          {(chainData.status === "rejected" || chainData.status === "cancelled") && (
            <Button size="sm" data-testid="new-approval-chain-btn" onClick={() => setSendOpen(true)} className="bg-slate-900 hover:bg-slate-800">
              <Stamp className="mr-1.5 h-4 w-4" /> Create New Approval Chain
            </Button>
          )}
        </div>
      )}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-xl" data-testid="send-approval-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send for Approval</DialogTitle>
            <DialogDescription>Build a sequential approval chain. Each approver is activated only after the previous one approves.</DialogDescription>
          </DialogHeader>
          <form onSubmit={send} className="space-y-4">
            <div>
              <Label>Approval Chain (in order) *</Label>
              <div className="mt-1.5 space-y-1" data-testid="approval-chain-builder">
                {chain.map((a, i) => (
                  <div key={a.id} className="flex items-center gap-2 rounded-md border px-3 py-2" data-testid={`chain-step-${i}`}>
                    <span className="font-mono text-xs text-muted-foreground">{i + 1}.</span>
                    <span className="flex-1 text-sm font-medium">{a.name} <span className="text-xs font-normal text-muted-foreground">· {a.designation}</span></span>
                    <button type="button" data-testid={`chain-up-${i}`} onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-900"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`chain-down-${i}`} onClick={() => move(i, 1)} className="text-slate-400 hover:text-slate-900"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`chain-remove-${i}`} onClick={() => setChain(chain.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
              <Select onValueChange={(v) => { const a = activeApprovers.find((x) => x.id === v); if (a && !chain.some((c) => c.id === a.id)) setChain([...chain, a]); }}>
                <SelectTrigger data-testid="add-to-chain-select" className="mt-2"><SelectValue placeholder="+ Add approver to chain" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {activeApprovers.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`chain-add-${a.id.slice(0, 8)}`}>{a.name} — {a.designation}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Buyer Comment (optional)</Label><Textarea data-testid="approval-buyer-comment" rows={2} className="mt-1.5" placeholder="Please review the commercial comparison and technical evaluation and approve the recommended supplier." value={comment} onChange={(e) => setComment(e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Approval Deadline (optional)</Label><Input data-testid="approval-deadline-input" type="datetime-local" className="mt-1.5" value={deadline} onChange={(e) => setDeadline(e.target.value)} /></div>
              <div><Label>Recommended Seller (optional)</Label>
                <Select value={recSeller} onValueChange={setRecSeller}>
                  <SelectTrigger data-testid="recommended-seller-select" className="mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {participated.map((p) => <SelectItem key={p.id} value={p.supplier_company_id}>{p.supplier_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {recSeller && <div><Label>Recommendation Reason</Label><Input data-testid="recommend-reason" className="mt-1.5" placeholder="Best combination of price, technical compliance and delivery." value={recReason} onChange={(e) => setRecReason(e.target.value)} /></div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="approval-send-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send for Approval
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignTarget} onOpenChange={() => setReassignTarget(null)}>
        <DialogContent className="bg-white" data-testid="reassign-approver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reassign Approver</DialogTitle>
            <DialogDescription>Step {reassignTarget?.sequence_number} is currently with {reassignTarget?.approver_name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!reassignForm.new_approver_id) return toast.error("Select a new approver");
            setSaving(true);
            try {
              await api.post(`/enquiries/${enquiryId}/approval/steps/${reassignTarget.id}/reassign`, reassignForm);
              toast.success("Step reassigned");
              setReassignTarget(null);
              load();
            } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
          }} className="space-y-4">
            <div>
              <Label>New Approver *</Label>
              <div className="mt-1.5 space-y-1 rounded-md border p-2" data-testid="reassign-approver-list">
                {activeApprovers.filter((a) => a.id !== reassignTarget?.approver_id).map((a) => (
                  <label key={a.id} className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                    <input type="radio" name="new-approver" data-testid={`reassign-approver-${a.id.slice(0, 8)}`} checked={reassignForm.new_approver_id === a.id}
                      onChange={() => setReassignForm({ ...reassignForm, new_approver_id: a.id })} className="accent-slate-900" />
                    <span className="font-medium">{a.name}</span>
                    <span className="text-xs text-muted-foreground">{a.department}</span>
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Reason *</Label><Input data-testid="reassign-approver-reason" required className="mt-1.5" placeholder="Original approver is on leave." value={reassignForm.reason} onChange={(e) => setReassignForm({ ...reassignForm, reason: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReassignTarget(null)}>Cancel</Button>
              <Button type="submit" data-testid="reassign-approver-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">Reassign</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
